import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { Add, GetApp } from "@material-ui/icons";
import { Pagination } from "@material-ui/lab";
import Layout from "components/layout/Layout";
import ColumnSelector from "components/package/ColumnSelector";
import PackageFilter from "components/package/PackageFilter";
import PackageList, { ColumnType } from "components/package/PackageList";
import { ROWS_PER_PAGE_OPTIONS } from "config/constants";
import { RootState } from "config/store";
import { format } from "date-fns";
import { hu } from "date-fns/locale";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import usePermissions from "shared/hooks/usePermissions";
import {
  exportPackages,
  exportPackages2,
  exportPackages3,
  searchPackages,
} from "shared/network/package.api";
import { Package } from "shared/types";
import fileDownload from "shared/util/fileDownload";
import PackageTimeWindowModal from "./PackageTimeWindowModal";

export type IsDescType = "ASC" | "DESC";
export type RadioGroupType = "YES" | "NO" | "UNDEFINED";

export type FilterValues = {
  search?: string;
  sort?: string;
  isDesc?: IsDescType;
  sourceDateFrom?: string;
  sourceDateTo?: string;
  createdOnFrom?: string;
  createdOnTo?: string;
  statuses?: string[];
  targetDateFrom?: string;
  targetDateTo?: string;
  fromSuccessfulDeliveryTime?: string;
  toSuccessfulDeliveryTime?: string;
  vehicleId?: number;
  warehouseId?: number;
  shopId?: number;
  trackingNumber?: string;
  sourceAddressZipCode?: string;
  sourceAddressCity?: string;
  sourceAddressAreaName?: string;
  sourceAddressAreaType?: string;
  sourceAddressHouseNumber?: string;
  currentAddressZipCode?: string;
  currentAddressCity?: string;
  currentAddressAreaName?: string;
  currentAddressAreaType?: string;
  currentAddressHouseNumber?: string;
  targetAddressZipCode?: string;
  targetAddressCity?: string;
  targetAddressAreaName?: string;
  targetAddressAreaType?: string;
  targetAddressHouseNumber?: string;
  shopPackagesNumber?: string;
  customerName?: string;
  companyId?: number;
  deliveryCompanyId?: number;
  targetDateNull?: RadioGroupType;
  isAskLoader?: RadioGroupType;
  isDeliveryToWarehouse?: RadioGroupType;
  failedDelivery?: RadioGroupType;
  isHazardous?: RadioGroupType;
  isFragile?: RadioGroupType;
  isStackable?: RadioGroupType;
  isCashOnDelivery?: RadioGroupType;
  isExpressDelivery?: RadioGroupType;
  isPUP?: RadioGroupType;
  plannedToTour?: RadioGroupType;
  markedToReturn?: RadioGroupType;
  ignoreStatuses?: boolean;
};

const PackageListView = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(
    parseInt(window.sessionStorage.getItem("PackagePageNumber") || JSON.stringify(0)),
  );
  const [pageSize, setPageSize] = useState<number>(
    parseInt(window.sessionStorage.getItem("PackagePageSize") || JSON.stringify(10)),
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<FilterValues>({
    sort: "CREATED_ON",
    isDesc: "DESC",
  });
  const { isCustomerService, isAdmin, checkPermissions } = usePermissions();
  const currentCompany = useSelector((state: RootState) => state.application.currentCompany);
  const location = useLocation();
  const showExportTest = new URLSearchParams(location.search).get("test") === "export";

  useEffect(() => {
    const savedFilters = sessionStorage.getItem("package-list-filter");
    if (savedFilters) {
      setFilters(JSON.parse(savedFilters));
    }
  }, []);

  const packageQuery = useQuery(
    ["packages", page, filters, pageSize],
    async () => {
      const { data } = await searchPackages({
        pageNumber: page,
        pageSize: pageSize,
        data: {
          ...filters,
          sort: filters.sort,
          isDesc: filters.isDesc === "DESC",
          ignoreStatuses: false,
          companyId:
            checkPermissions({
              requestedPermissions: ["SHOPKEEPER"],
            }) &&
            !isAdmin &&
            !isCustomerService
              ? currentCompany?.id
              : undefined,
          targetDateNull:
            filters.targetDateNull === "YES"
              ? true
              : filters.targetDateNull === "NO"
              ? false
              : undefined,
          isAskLoader:
            filters.isAskLoader === "YES" ? true : filters.isAskLoader === "NO" ? false : undefined,
          isHazardous:
            filters.isHazardous === "YES" ? true : filters.isHazardous === "NO" ? false : undefined,
          isFragile:
            filters.isFragile === "YES" ? true : filters.isFragile === "NO" ? false : undefined,
          isStackable:
            filters.isStackable === "YES" ? true : filters.isStackable === "NO" ? false : undefined,
          isCashOnDelivery:
            filters.isCashOnDelivery === "YES"
              ? true
              : filters.isCashOnDelivery === "NO"
              ? false
              : undefined,
          isExpressDelivery:
            filters.isExpressDelivery === "YES"
              ? true
              : filters.isExpressDelivery === "NO"
              ? false
              : undefined,
          plannedToTour:
            filters.plannedToTour === "YES"
              ? true
              : filters.plannedToTour === "NO"
              ? false
              : undefined,
          markedToReturn:
            filters.markedToReturn === "YES"
              ? true
              : filters.markedToReturn === "NO"
              ? false
              : undefined,
          isPUP: filters.isPUP === "YES" ? true : filters.isPUP === "NO" ? false : undefined,
          isDeliveryToWarehouse:
            filters.isDeliveryToWarehouse === "YES"
              ? true
              : filters.isDeliveryToWarehouse === "NO"
              ? false
              : undefined,
          failedDelivery:
            filters.failedDelivery === "YES"
              ? true
              : filters.failedDelivery === "NO"
              ? false
              : undefined,
        },
      });
      return data;
    },
    { enabled: !!currentCompany },
  );

  const downloadMutation = useMutation(
    async () => {
      const { data } = await exportPackages({
        ...filters,
        sort: filters.sort,
        isDesc: filters.isDesc === "DESC",
        ignoreStatuses: false,
        companyId:
          checkPermissions({
            requestedPermissions: ["SHOPKEEPER"],
          }) && !isAdmin
            ? currentCompany?.id
            : undefined,
        targetDateNull:
          filters.targetDateNull === "YES"
            ? true
            : filters.targetDateNull === "NO"
            ? false
            : undefined,
        isAskLoader:
          filters.isAskLoader === "YES" ? true : filters.isAskLoader === "NO" ? false : undefined,
        isHazardous:
          filters.isHazardous === "YES" ? true : filters.isHazardous === "NO" ? false : undefined,
        isFragile:
          filters.isFragile === "YES" ? true : filters.isFragile === "NO" ? false : undefined,
        isStackable:
          filters.isStackable === "YES" ? true : filters.isStackable === "NO" ? false : undefined,
        isCashOnDelivery:
          filters.isCashOnDelivery === "YES"
            ? true
            : filters.isCashOnDelivery === "NO"
            ? false
            : undefined,
        isExpressDelivery:
          filters.isExpressDelivery === "YES"
            ? true
            : filters.isExpressDelivery === "NO"
            ? false
            : undefined,
        plannedToTour:
          filters.plannedToTour === "YES"
            ? true
            : filters.plannedToTour === "NO"
            ? false
            : undefined,
        markedToReturn:
          filters.markedToReturn === "YES"
            ? true
            : filters.markedToReturn === "NO"
            ? false
            : undefined,
        isDeliveryToWarehouse:
          filters.isDeliveryToWarehouse === "YES"
            ? true
            : filters.isDeliveryToWarehouse === "NO"
            ? false
            : undefined,
        isPUP: filters.isPUP === "YES" ? true : filters.isPUP === "NO" ? false : undefined,
        failedDelivery:
          filters.isDeliveryToWarehouse === "YES"
            ? true
            : filters.isDeliveryToWarehouse === "NO"
            ? false
            : undefined,
      });
      return data;
    },
    {
      onSuccess: data => {
        fileDownload(data, `Csomagok (${format(new Date(), "P", { locale: hu })}).xls`);
      },
    },
  );

  const downloadMutation2 = useMutation(
    async () => {
      const { data } = await exportPackages2({
        ...filters,
        sort: filters.sort,
        isDesc: filters.isDesc === "DESC",
        ignoreStatuses: false,
        companyId:
          checkPermissions({
            requestedPermissions: ["SHOPKEEPER"],
          }) && !isAdmin
            ? currentCompany?.id
            : undefined,
        targetDateNull:
          filters.targetDateNull === "YES"
            ? true
            : filters.targetDateNull === "NO"
            ? false
            : undefined,
        isAskLoader:
          filters.isAskLoader === "YES" ? true : filters.isAskLoader === "NO" ? false : undefined,
        isHazardous:
          filters.isHazardous === "YES" ? true : filters.isHazardous === "NO" ? false : undefined,
        isFragile:
          filters.isFragile === "YES" ? true : filters.isFragile === "NO" ? false : undefined,
        isStackable:
          filters.isStackable === "YES" ? true : filters.isStackable === "NO" ? false : undefined,
        isCashOnDelivery:
          filters.isCashOnDelivery === "YES"
            ? true
            : filters.isCashOnDelivery === "NO"
            ? false
            : undefined,
        isExpressDelivery:
          filters.isExpressDelivery === "YES"
            ? true
            : filters.isExpressDelivery === "NO"
            ? false
            : undefined,
        plannedToTour:
          filters.plannedToTour === "YES"
            ? true
            : filters.plannedToTour === "NO"
            ? false
            : undefined,
        markedToReturn:
          filters.markedToReturn === "YES"
            ? true
            : filters.markedToReturn === "NO"
            ? false
            : undefined,
        isDeliveryToWarehouse:
          filters.isDeliveryToWarehouse === "YES"
            ? true
            : filters.isDeliveryToWarehouse === "NO"
            ? false
            : undefined,
        isPUP: filters.isPUP === "YES" ? true : filters.isPUP === "NO" ? false : undefined,
        failedDelivery:
          filters.isDeliveryToWarehouse === "YES"
            ? true
            : filters.isDeliveryToWarehouse === "NO"
            ? false
            : undefined,
      });
      return data;
    },
    {
      onSuccess: data => {
        fileDownload(data, `Csomagok (${format(new Date(), "P", { locale: hu })}).xls`);
      },
    },
  );

  const downloadMutation3 = useMutation(
    async () => {
      const { data } = await exportPackages3({
        ...filters,
        sort: filters.sort,
        isDesc: filters.isDesc === "DESC",
        ignoreStatuses: false,
        companyId:
          checkPermissions({
            requestedPermissions: ["SHOPKEEPER"],
          }) && !isAdmin
            ? currentCompany?.id
            : undefined,
        targetDateNull:
          filters.targetDateNull === "YES"
            ? true
            : filters.targetDateNull === "NO"
            ? false
            : undefined,
        isAskLoader:
          filters.isAskLoader === "YES" ? true : filters.isAskLoader === "NO" ? false : undefined,
        isHazardous:
          filters.isHazardous === "YES" ? true : filters.isHazardous === "NO" ? false : undefined,
        isFragile:
          filters.isFragile === "YES" ? true : filters.isFragile === "NO" ? false : undefined,
        isStackable:
          filters.isStackable === "YES" ? true : filters.isStackable === "NO" ? false : undefined,
        isCashOnDelivery:
          filters.isCashOnDelivery === "YES"
            ? true
            : filters.isCashOnDelivery === "NO"
            ? false
            : undefined,
        isExpressDelivery:
          filters.isExpressDelivery === "YES"
            ? true
            : filters.isExpressDelivery === "NO"
            ? false
            : undefined,
        plannedToTour:
          filters.plannedToTour === "YES"
            ? true
            : filters.plannedToTour === "NO"
            ? false
            : undefined,
        markedToReturn:
          filters.markedToReturn === "YES"
            ? true
            : filters.markedToReturn === "NO"
            ? false
            : undefined,
        isDeliveryToWarehouse:
          filters.isDeliveryToWarehouse === "YES"
            ? true
            : filters.isDeliveryToWarehouse === "NO"
            ? false
            : undefined,
        isPUP: filters.isPUP === "YES" ? true : filters.isPUP === "NO" ? false : undefined,
        failedDelivery:
          filters.isDeliveryToWarehouse === "YES"
            ? true
            : filters.isDeliveryToWarehouse === "NO"
            ? false
            : undefined,
      });
      return data;
    },
    {
      onSuccess: data => {
        fileDownload(data, `Csomagok (${format(new Date(), "P", { locale: hu })}).xls`);
      },
    },
  );

  const [columnOpen, setColumnOpen] = useState(false);
  const [columns, setColumns] = useState<ColumnType>({
    status: true,
    packageName: false,
    trackingNumber: true,
    volume: false,
    storageCode: false,
    weight: false,
    icons: true,
    companyName: false,
    shopPackageNumber: false,
    customerComment: false,
    customerEmail: false,
    customerName: false,
    customerPhone: false,
    sourceAddress: false,
    currentAddress: false,
    targetAddress: true,
    targetFromDate: false,
    targetToDate: false,
    sourceFromDate: false,
    sourceToDate: false,
    returnGoods: false,
    cduNumber: false,
    orderShopNumber: false,
    orderExternalNumber: false,
    plannedToTour: false,
  });

  useEffect(() => {
    const temp = localStorage.getItem("packagelist-package-columns");
    if (temp) {
      setColumns(JSON.parse(temp));
    }
    window.sessionStorage.setItem("PackagePageNumber", JSON.stringify(page));
  }, [page]);

  const [enableMultiSelect, setEnableMultiSelect] = useState(false);
  const [selectedPackages, setSelectedPackages] = useState<Package[]>([]);
  const [timeWindowOpen, setTimeWindowOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportDialogData, setExportDialogData] = useState({
    exportError: "",
    canExport: false,
  });

  function hasValue(value: any) {
    if (value === undefined || value === null) {
      return false;
    }

    if (typeof value === "string") {
      if (value === "UNDEFINED") {
        return false;
      }

      return value.trim().length > 0;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return true;
  }

  function isRangeWithinTwoMonths(fromDate?: string, toDate?: string) {
    if (!fromDate || !toDate) {
      return false;
    }

    const from = new Date(fromDate);
    const to = new Date(toDate);

    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || to < from) {
      return false;
    }

    const maxTo = new Date(from);
    maxTo.setMonth(maxTo.getMonth() + 2);

    return to <= maxTo;
  }

  function validateExport(currentFilters: FilterValues) {
    // TESTING PURPOSES ONLY: allow export for localhost and arrakis.dev.promera.systems
    // if (["localhost", "arrakis.dev.promera.systems"].includes(window.location.hostname)) {
    //   return { canExport: true, error: "" };
    // }

    const ranges = [
      {
        label: "Felvételi időablak kezdete - Felvételi időszak vége",
        from: currentFilters.sourceDateFrom,
        to: currentFilters.sourceDateTo,
      },
      {
        label: "Kiszállítási időablak kezdete - Kiszállítási időszak vége",
        from: currentFilters.targetDateFrom,
        to: currentFilters.targetDateTo,
      },
      {
        label: "Aktuális státusz idő kezdete - Aktuális státusz idő vége",
        from: currentFilters.fromSuccessfulDeliveryTime,
        to: currentFilters.toSuccessfulDeliveryTime,
      },
      {
        label: "Hozzáadás ideje ettől - Hozzáadás ideje eddig",
        from: currentFilters.createdOnFrom,
        to: currentFilters.createdOnTo,
      },
    ];

    const hasPartialRange = ranges.some(range => hasValue(range.from) !== hasValue(range.to));

    if (hasPartialRange) {
      return {
        canExport: false,
        error: "A dátumszűrők csak kezdő és záró dátummal együtt érvényesek.",
      };
    }

    const filledRanges = ranges.filter(range => hasValue(range.from) && hasValue(range.to));

    if (filledRanges.length === 0) {
      return {
        canExport: false,
        error: "Nincsenek megfelelő szűrők kiválasztva.",
      };
    }

    const allRangesValid = filledRanges.every(range =>
      isRangeWithinTwoMonths(range.from, range.to),
    );

    if (!allRangesValid) {
      return {
        canExport: false,
        error: "A kiválasztott dátumintervallum nagyobb mint 2 hónap.",
      };
    }

    return {
      canExport: true,
      error: "",
    };
  }

  const formatters: Record<string, (val: any) => string> = {
    sourceDateFrom: val => format(new Date(val), "yyyy.MM.dd. HH:mm"),
    sourceDateTo: val => format(new Date(val), "yyyy.MM.dd. HH:mm"),
    targetDateFrom: val => format(new Date(val), "yyyy.MM.dd. HH:mm"),
    targetDateTo: val => format(new Date(val), "yyyy.MM.dd. HH:mm"),
    createdOnFrom: val => format(new Date(val), "yyyy.MM.dd. HH:mm"),
    createdOnTo: val => format(new Date(val), "yyyy.MM.dd. HH:mm"),
    fromSuccessfulDeliveryTime: val => format(new Date(val), "yyyy.MM.dd. HH:mm"),
    toSuccessfulDeliveryTime: val => format(new Date(val), "yyyy.MM.dd. HH:mm"),
    targetDateNull: val => (val === "YES" ? "Igen" : "Nem"),
    isPUP: val => (val === "YES" ? "Igen" : "Nem"),
    isAskLoader: val => (val === "YES" ? "Igen" : "Nem"),
    isHazardous: val => (val === "YES" ? "Igen" : "Nem"),
    isFragile: val => (val === "YES" ? "Igen" : "Nem"),
    isStackable: val => (val === "YES" ? "Igen" : "Nem"),
    isCashOnDelivery: val => (val === "YES" ? "Igen" : "Nem"),
    isExpressDelivery: val => (val === "YES" ? "Igen" : "Nem"),
    plannedToTour: val => (val === "YES" ? "Igen" : "Nem"),
    markedToReturn: val => (val === "YES" ? "Igen" : "Nem"),
    isDeliveryToWarehouse: val => (val === "YES" ? "Igen" : "Nem"),
    failedDelivery: val => (val === "YES" ? "Igen" : "Nem"),
    sourceAddressAreaType: val => t(`common:areaType.${val}`),
    currentAddressAreaType: val => t(`common:areaType.${val}`),
    targetAddressAreaType: val => t(`common:areaType.${val}`),
    sort: val => t(`package.sort.${val}`),
    isDesc: val => (val === "DESC" ? t("package.desc") : t("package.asc")),
  };

  function getFilterEntries(currentFilters: FilterValues) {
    return Object.entries(currentFilters)
      .filter(([key, value]) => key !== "ignoreStatuses" && hasValue(value))
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return {
            key,
            label: t(`packageFilters.${key}`),
            value: `${value.length} db`,
          };
        }

        const formatFn = formatters[key] || String;

        return {
          key,
          label: t(`packageFilters.${key}`),
          value: formatFn(value),
        };
      });
  }

  function resetMultiSelection() {
    setEnableMultiSelect(false);
    setSelectedPackages([]);
    packageQuery.refetch();
  }

  function addPackage(pack: Package) {
    setSelectedPackages(selectedPackages => [...selectedPackages, pack]);
  }

  function removePackage(pack: Package) {
    setSelectedPackages(selectedPackages.filter(entry => entry.id !== pack.id));
  }

  function startMultiPlanning() {
    setTimeWindowOpen(true);
  }

  function handleExportClick() {
    const exportValidation = validateExport(filters);

    setExportDialogData({
      exportError: exportValidation.error,
      canExport: exportValidation.canExport,
    });
    setExportDialogOpen(true);
  }

  function handleExportConfirm() {
    if (!exportDialogData.canExport) {
      return;
    }

    setExportDialogOpen(false);
    downloadMutation.mutate();
  }

  function handleExportConfirm2() {
    if (!exportDialogData.canExport) {
      return;
    }

    setExportDialogOpen(false);
    downloadMutation2.mutate();
  }
  function handleExportConfirm3() {
    if (!exportDialogData.canExport) {
      return;
    }

    setExportDialogOpen(false);
    downloadMutation3.mutate();
  }

  const filterEntries = getFilterEntries(filters);
  return (
    <Layout
      title={t("package.list")}
      actionButton={
        <>
          {(checkPermissions({
            requestedPermissions: ["SHOPKEEPER"],
          }) ||
            isAdmin ||
            isCustomerService) && (
            <>
              <Button
                onClick={handleExportClick}
                variant="outlined"
                color="primary"
                style={{ height: 40, marginRight: 8 }}
                startIcon={
                  downloadMutation.isLoading ? (
                    <CircularProgress style={{ height: 20, width: 20 }} />
                  ) : (
                    <GetApp />
                  )
                }
                disabled={(downloadMutation.isLoading, getFilterEntries(filters).length <= 2)}
              >
                {t("common:button.export")}
              </Button>

              <Button
                size="medium"
                color="primary"
                variant="outlined"
                component={Link}
                to="/package/create"
                startIcon={<Add />}
                style={{ height: 40 }}
              >
                {t("package.create")}
              </Button>
            </>
          )}
        </>
      }
    >
      <ColumnSelector
        type="packagelist"
        open={columnOpen}
        columns={columns}
        setColumns={setColumns}
        setColumnOpen={setColumnOpen}
      />
      <PackageFilter
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={filters}
        setFilters={setFilters}
        setPage={setPage}
        filterOpen={filterOpen}
        setFilterOpen={setFilterOpen}
        columnOpen={columnOpen}
        setColumnOpen={setColumnOpen}
        enableMultiSelect={enableMultiSelect}
        multiSelectType={"time"}
        setEnableMultiSelect={setEnableMultiSelect}
        startMultiPlanning={startMultiPlanning}
        packagesSelected={!isEmpty(selectedPackages)}
        resetMultiSelection={resetMultiSelection}
        storageKey="package-list-filter"
      />
      <PackageList
        columns={columns}
        packages={packageQuery.data?.page.content || []}
        refetch={packageQuery.refetch}
        loading={packageQuery.isFetching}
        enableMultiSelect={enableMultiSelect}
        selectedPackageList={selectedPackages}
        addPackage={addPackage}
        removePackage={removePackage}
        setSelectedPackages={setSelectedPackages}
      />
      {packageQuery.data && (
        <Box display="flex" justifyContent="center" alignItems="center" gridGap={8}>
          <Box>
            <TextField
              margin="none"
              select
              defaultValue={pageSize}
              size="small"
              onChange={event => {
                setPageSize(Number.parseInt(event.target.value));
                setPage(0);
                sessionStorage.setItem("PackagePageSize", event.target.value);
                sessionStorage.setItem("PackagePageNumber", "0");
              }}
            >
              {ROWS_PER_PAGE_OPTIONS.map((option, index) => {
                return (
                  <MenuItem value={option} key={index}>
                    {option}
                  </MenuItem>
                );
              })}
            </TextField>
          </Box>
          <Pagination
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "24px 0",
            }}
            count={packageQuery?.data?.page.totalPages}
            color="primary"
            page={packageQuery.data.page.number + 1}
            onChange={(e, page) => {
              sessionStorage.setItem("PackagePageNumber", JSON.stringify(page - 1));
              setPage(page - 1);
            }}
          />
        </Box>
      )}
      <PackageTimeWindowModal
        open={timeWindowOpen}
        setOpen={setTimeWindowOpen}
        packageList={selectedPackages}
        refetch={resetMultiSelection}
      />
      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Exportálás megerősítése</DialogTitle>
        <DialogContent>
          {exportDialogData.exportError && (
            <Typography color="error" style={{ marginBottom: 12 }}>
              {exportDialogData.exportError}
            </Typography>
          )}

          <Typography style={{ marginBottom: 8 }}>Kiválasztott szűrők:</Typography>
          {filterEntries.length === 0 ? (
            <Typography color="textSecondary">Nincs aktív szűrő.</Typography>
          ) : (
            filterEntries.map(filterEntry => (
              <Typography key={filterEntry.key} variant="body2" style={{ marginBottom: 4 }}>
                {filterEntry.label}: {filterEntry.value}
              </Typography>
            ))
          )}

          <Typography style={{ marginTop: 12 }}>Biztosan exportálni szeretnéd?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} color="default">
            Mégse
          </Button>
          <Button
            onClick={handleExportConfirm}
            color="primary"
            variant="contained"
            disabled={!exportDialogData.canExport}
          >
            Exportálás
          </Button>
          {showExportTest && (
            <Button
              onClick={handleExportConfirm2}
              color="primary"
              variant="contained"
              disabled={!exportDialogData.canExport}
            >
              Exportálás 2
            </Button>
          )}
          {showExportTest && (
            <Button
              onClick={handleExportConfirm3}
              color="primary"
              variant="contained"
              disabled={!exportDialogData.canExport}
            >
              Exportálás 3
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default PackageListView;
